'use client'

/**
 * Document URL Sync Hook
 *
 * Manages environment-specific document URL synchronization.
 * Each environment (localhost, preview, production) has its own document.
 * Fetches document URL from user profile on app initialization
 * and syncs it to localStorage for local access.
 *
 * Flow:
 * 1. Determine current environment origin
 * 2. Check localStorage (fast path - already have URL for this environment)
 * 3. If empty, fetch from user profile (cross-device discovery for this environment)
 * 4. Save to localStorage for subsequent loads
 * 5. Return current document URL
 *
 * This enables:
 * - Each environment to have independent documents
 * - Multiple devices to sync within same environment
 * - Preview deployments to work without localhost data
 */

import { useState, useEffect } from 'react'
import { getAutomergeDocumentUrl } from './actions'
import { getEnvironmentStorageKey, getCurrentOrigin } from '@/lib/environment'
import { createClient } from '@/account/client'

// Lazy evaluation - called only in browser context
const getStorageKey = () => getEnvironmentStorageKey('automerge-doc-url')

interface DocumentUrlSyncResult {
  /** Current document URL (null if not yet initialized) */
  documentUrl: string | null
  /** Whether initial sync check is complete */
  ready: boolean
  /** Whether actively fetching from profile */
  loading: boolean
  /** Whether the profile was successfully queried (false = network/auth error) */
  profileChecked: boolean
  /** Whether the user is authenticated (false = skip Automerge initialization) */
  isAuthenticated: boolean
}

/**
 * Hook to sync document URL from profile to localStorage on app initialization.
 *
 * Call this in AutomergeProvider before creating the repo to ensure
 * cross-device document discovery works.
 *
 * @returns Document URL sync state
 *
 * @example
 * ```tsx
 * function AutomergeProvider({ children }: Props) {
 *   const { documentUrl, ready } = useDocumentUrlSync()
 *
 *   useEffect(() => {
 *     if (ready) {
 *       // Now safe to initialize repo - documentUrl has been synced
 *       getRepo().then(setRepo)
 *     }
 *   }, [ready])
 *
 *   // ...
 * }
 * ```
 */
export function useDocumentUrlSync(): DocumentUrlSyncResult {
  const [documentUrl, setDocumentUrl] = useState<string | null>(null)
  const [ready, setReady] = useState(false)
  const [loading, setLoading] = useState(true)
  const [profileChecked, setProfileChecked] = useState(false)
  const [isAuthenticated, setIsAuthenticated] = useState(true)

  useEffect(() => {
    async function syncDocumentUrl() {
      try {
        // Check auth first — skip entirely for unauthenticated users
        const supabase = createClient()
        const {
          data: { session },
        } = await supabase.auth.getSession()

        if (!session) {
          console.log('[DocumentUrlSync] Not authenticated — skipping')
          setIsAuthenticated(false)
          setReady(true)
          setLoading(false)
          return
        }

        const origin = getCurrentOrigin()
        const storageKey = getStorageKey()

        console.log('[DocumentUrlSync] Starting sync...', {
          origin,
          storageKey,
        })

        // Step 1: Check localStorage (fast path)
        const localUrl = localStorage.getItem(storageKey)

        if (localUrl) {
          console.log(
            '[DocumentUrlSync] Found URL in localStorage:',
            localUrl.slice(0, 30) + '...',
          )
          setDocumentUrl(localUrl)
          setProfileChecked(true) // localStorage has URL — safe for initRepo (no doc creation needed)
          setReady(true)
          setLoading(false)

          // Step 1.5: Verify profile matches localStorage (prevent cross-user contamination)
          // Profile is per-user (source of truth), localStorage is per-machine (cache).
          // If profile has a different URL, profile wins (likely a user switch without sign-out).
          // If profile has NO URL, sync localStorage → profile (handles profile save failures,
          // preserves the reference to IndexedDB data).
          getAutomergeDocumentUrl(origin)
            .then(async (profileUrl) => {
              if (profileUrl && profileUrl !== localUrl) {
                console.log(
                  '[DocumentUrlSync] Profile URL differs from localStorage, profile wins (likely user switch)',
                )
                localStorage.setItem(storageKey, profileUrl)
                setDocumentUrl(profileUrl)
              } else if (!profileUrl) {
                // Profile has no URL — likely a failed profile save. Sync localStorage → profile
                // to recover the reference. We do NOT clear localStorage here because that would
                // orphan the IndexedDB data. Cross-user contamination is handled by clearing
                // localStorage on sign-out instead.
                console.log(
                  '[DocumentUrlSync] Profile has no URL, syncing localStorage → profile',
                )
                const { saveAutomergeDocumentUrl } = await import('./actions')
                await saveAutomergeDocumentUrl(localUrl, origin)
              }
            })
            .catch((error) => {
              console.warn(
                '[DocumentUrlSync] Failed to verify profile document URL:',
                error,
              )
            })

          return
        }

        console.log(
          '[DocumentUrlSync] No URL in localStorage, fetching from profile...',
        )

        // Step 2: Fetch from user profile (cross-device discovery for this environment)
        const profileUrl = await getAutomergeDocumentUrl(origin)

        console.log('[DocumentUrlSync] Profile returned:', {
          hasUrl: !!profileUrl,
          url: profileUrl?.slice(0, 30) + '...',
          origin,
        })

        if (profileUrl) {
          // Save to localStorage for fast access next time
          localStorage.setItem(storageKey, profileUrl)
          setDocumentUrl(profileUrl)
          console.log('[DocumentUrlSync] Saved URL to localStorage')
        } else {
          // No URL in profile or localStorage for this environment - new user or first use of this environment
          console.log(
            '[DocumentUrlSync] No URL found - treating as new user for this environment',
          )
          setDocumentUrl(null)
        }

        setProfileChecked(true) // Profile query succeeded (even if no URL found)
        setReady(true)
        setLoading(false)
      } catch (error) {
        console.error('[DocumentUrlSync] Failed to sync document URL:', error)
        // profileChecked stays false — initRepo must NOT create a new document
        // because we couldn't verify the profile is empty
        setDocumentUrl(null)
        setReady(true)
        setLoading(false)
      }
    }

    syncDocumentUrl()
  }, [])

  return { documentUrl, ready, loading, profileChecked, isAuthenticated }
}
